<template>
  <el-card>
    <template #header>
      <div class="head">
        <span>权限分配</span>
        <div class="head-actions">
          <el-input
            v-model="searchKeyword"
            placeholder="搜索姓名 / 用户名 / 部门"
            clearable
            style="width: 260px"
          />
          <el-button v-if="canConfigTaskGrants" type="primary" plain @click="taskGrantOpen = true">
            任务查看矩阵
          </el-button>
          <el-button
            v-if="canConfigInboundNotify"
            type="primary"
            plain
            @click="inboundNotifyOpen = true"
          >
            入库通知设置
          </el-button>
          <el-button
            v-if="canConfigSharedTaskAssignees"
            type="primary"
            plain
            @click="sharedTaskAssigneesOpen = true"
          >
            库位/数量任务责任人
          </el-button>
          <el-button @click="loadAll">刷新</el-button>
        </div>
      </div>
    </template>

    <el-alert
      type="info"
      :closable="false"
      show-icon
      class="board-tip"
      title="任务看板头像"
      description="下列开关控制该账号是否出现在任务中心人员负荷墙（他人可见其待办角标）。关闭后对他人隐藏；该用户本人登录仍能看到自己的头像。"
    />

    <el-table :data="filteredUsers" v-loading="loading" stripe>
      <el-table-column prop="user_id" label="ID" width="70" />
      <el-table-column prop="full_name" label="姓名" width="140" />
      <el-table-column prop="username" label="用户名" width="140" />
      <el-table-column prop="department" label="部门" width="140" />
      <el-table-column label="权限摘要" min-width="280">
        <template #default="{ row }">
          <div class="summary-tags">
            <el-tag
              v-for="item in buildUserPermissionSummary(row)"
              :key="`${row.user_id}-${item.label}`"
              size="small"
              :type="item.type"
            >
              {{ item.label }}
            </el-tag>
            <span v-if="!buildUserPermissionSummary(row).length">-</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="已分配角色" min-width="160">
        <template #default="{ row }">
          <el-tag v-for="r in (row._roles || [])" :key="r.role_id" size="small" style="margin-right:6px">
            {{ r.role_name }}
          </el-tag>
          <span v-if="!(row._roles || []).length">-</span>
        </template>
      </el-table-column>
      <el-table-column label="任务看板显示" width="120" align="center">
        <template #default="{ row }">
          <el-switch
            v-model="row.show_on_task_board"
            :disabled="!row.is_active || boardSavingId === row.user_id"
            active-text="显示"
            inactive-text="隐藏"
            inline-prompt
            @change="(v) => onBoardVisibilityChange(row, v)"
          />
        </template>
      </el-table-column>
      <el-table-column label="操作" width="340" fixed="right">
        <template #default="{ row }">
          <el-button size="small" @click="openAssign(row)">分配角色</el-button>
          <el-button size="small" type="primary" @click="showEffective(row)">权限矩阵</el-button>
        </template>
      </el-table-column>
    </el-table>
  </el-card>

  <el-dialog v-model="showAssignDialog" :title="`分配角色 - ${targetUser?.full_name || ''}`" width="560px">
    <el-checkbox-group v-model="selectedRoleIds">
      <el-checkbox v-for="r in roles" :key="r.role_id" :label="r.role_id">
        {{ r.role_name }} ({{ r.role_code || '-' }})
      </el-checkbox>
    </el-checkbox-group>
    <template #footer>
      <el-button @click="showAssignDialog = false">取消</el-button>
      <el-button type="primary" :loading="assigning" @click="submitAssign">保存</el-button>
    </template>
  </el-dialog>

  <el-dialog v-model="showEffectiveDialog" :title="`权限矩阵 - ${targetUser?.full_name || ''}`" width="920px">
    <el-alert title="生效权限 = 角色权限 + 管理员内置权限（管理员/超管）" type="info" :closable="false" style="margin-bottom:10px" />
    <div v-if="effectivePermissionMatrix.length">
      <el-tabs v-model="activeMatrixModule">
        <el-tab-pane
          v-for="group in effectivePermissionMatrix"
          :key="group.moduleKey"
          :label="group.moduleLabel"
          :name="group.moduleKey"
        >
          <el-table :data="group.units" border stripe>
            <el-table-column prop="unitLabel" label="功能单元" min-width="180" />
            <el-table-column label="查看" width="90">
              <template #default="{ row }">
                <el-tag :type="row.view ? 'success' : 'info'">{{ row.view ? '有' : '无' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="编辑" width="90">
              <template #default="{ row }">
                <el-tag :type="row.edit ? 'warning' : 'info'">{{ row.edit ? '有' : '无' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="审核" width="90">
              <template #default="{ row }">
                <el-tag :type="row.review ? 'danger' : 'info'">{{ row.review ? '有' : '无' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="数据范围" min-width="180">
              <template #default="{ row }">
                <el-tag v-for="scope in row.scopes" :key="scope" style="margin-right: 6px">{{ scope }}</el-tag>
                <span v-if="!row.scopes.length">全部/未单独限制</span>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </div>
    <div v-else>暂无权限</div>
  </el-dialog>

  <TaskViewGrantMatrix v-model="taskGrantOpen" />
  <InboundNotifyRecipients v-model="inboundNotifyOpen" />
  <SharedTaskAssignees v-model="sharedTaskAssigneesOpen" />
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/store/user'
import { canConfigureTaskViewGrants } from '@/utils/mobileTaskAccess'
import TaskViewGrantMatrix from '@/components/tasks/TaskViewGrantMatrix.vue'
import InboundNotifyRecipients from '@/components/tasks/InboundNotifyRecipients.vue'
import SharedTaskAssignees from '@/components/tasks/SharedTaskAssignees.vue'
import { getUsers } from '@/api/users'
import {
  getPermissionCatalog,
  getRoles,
  getTaskBoardVisibility,
  getUserEffectivePermissions,
  getUserRoles,
  setUserRoles,
  setUserTaskBoardVisibility
} from '@/api/rbac'

const userStore = useUserStore()
userStore.initFromStorage()

const taskGrantOpen = ref(false)
const inboundNotifyOpen = ref(false)
const sharedTaskAssigneesOpen = ref(false)
const canConfigTaskGrants = computed(() => canConfigureTaskViewGrants(userStore.user))
const canConfigInboundNotify = computed(() => canConfigureTaskViewGrants(userStore.user))
const canConfigSharedTaskAssignees = computed(() => canConfigureTaskViewGrants(userStore.user))

const loading = ref(false)
const assigning = ref(false)
const users = ref([])
const roles = ref([])
const permissionCatalog = ref({})
const showAssignDialog = ref(false)
const showEffectiveDialog = ref(false)
const targetUser = ref(null)
const selectedRoleIds = ref([])
const effectivePermissions = ref([])
const activeMatrixModule = ref('')
const searchKeyword = ref('')
const boardSavingId = ref(null)

const moduleLabelMap = {
  system: '系统板块',
  customers: '客户板块',
  masterdata: '基础资料板块',
  orders: '订单板块',
  purchase: '采购板块',
  inventory: '库存板块',
  finance: '财务板块'
}

const scopeLabelMap = {
  self: '本人',
  department: '本部门'
}

const filteredUsers = computed(() => {
  const keyword = searchKeyword.value.trim().toLowerCase()
  if (!keyword) return users.value
  return users.value.filter((user) => {
    return [user.full_name, user.username, user.department].some((value) =>
      String(value || '').toLowerCase().includes(keyword)
    )
  })
})

const effectivePermissionMatrix = computed(() => {
  const catalog = permissionCatalog.value || {}
  const keySet = new Set(effectivePermissions.value || [])
  const hasAllPermissions = keySet.has('*') || keySet.has('global.admin')
  const matrix = []

  Object.entries(catalog).forEach(([moduleKey, items]) => {
    const unitMap = {}
    ;(items || []).forEach((item) => {
      const unitLabel = item.unit || '默认单元'
      if (!unitMap[unitLabel]) {
        unitMap[unitLabel] = {
          unitLabel,
          view: false,
          edit: false,
          review: false,
          scopes: []
        }
      }
      if (!hasAllPermissions && !keySet.has(item.key)) return
      if (item.action === 'view') {
        unitMap[unitLabel].view = true
      } else if (item.action === 'edit') {
        unitMap[unitLabel].edit = true
      } else if (item.action === 'scope') {
        const scopeName = item.key.split('.').pop()
        unitMap[unitLabel].scopes.push(scopeLabelMap[scopeName] || scopeName)
      } else {
        unitMap[unitLabel].review = true
      }
    })
    const units = Object.values(unitMap).filter((unit) => unit.view || unit.edit || unit.review || unit.scopes.length)
    if (units.length) {
      matrix.push({
        moduleKey,
        moduleLabel: moduleLabelMap[moduleKey] || moduleKey,
        units
      })
    }
  })

  return matrix
})

const buildUserPermissionSummary = (row) => {
  if (row.is_admin || row.is_superuser) {
    return [
      { label: '系统管理员', type: 'danger' },
      { label: '全部权限', type: 'warning' }
    ]
  }

  const rolePermissions = new Set()
  for (const role of row._roles || []) {
    for (const key of role.permissions || []) {
      rolePermissions.add(key)
    }
  }

  const summary = []
  if (rolePermissions.has('customers.scope.self')) summary.push({ label: '客户仅本人', type: 'info' })
  if (rolePermissions.has('customers.scope.department')) summary.push({ label: '客户本部门', type: 'info' })
  if (rolePermissions.has('orders.scope.self')) summary.push({ label: '订单仅本人', type: 'info' })
  if (rolePermissions.has('orders.scope.department')) summary.push({ label: '订单本部门', type: 'info' })
  if (rolePermissions.has('purchase.scope.self')) summary.push({ label: '采购仅本人', type: 'success' })
  if (rolePermissions.has('purchase.scope.department')) summary.push({ label: '采购本部门', type: 'success' })
  if (rolePermissions.has('inventory.scope.self')) summary.push({ label: '库存仅本人', type: 'warning' })
  if (rolePermissions.has('inventory.scope.department')) summary.push({ label: '库存本部门', type: 'warning' })
  if (rolePermissions.has('orders.material_matching.review')) summary.push({ label: '可审配料', type: 'danger' })
  if (rolePermissions.has('orders.material_matching.unaudit')) summary.push({ label: '可取消配料/加工审核', type: 'warning' })
  if (rolePermissions.has('orders.material_matching.unlock_material')) summary.push({ label: '可解除配料料号锁定', type: 'danger' })
  if (rolePermissions.has('orders.material_matching.archive_no_bill')) summary.push({ label: '可免请款移出配料', type: 'warning' })
  if (rolePermissions.has('purchase.processing.append_input')) summary.push({ label: '可追加加工投入量', type: 'warning' })
  if (rolePermissions.has('orders.dispatch.review')) summary.push({ label: '可审派工', type: 'danger' })
  if (rolePermissions.has('orders.dispatch.unaudit')) summary.push({ label: '可取消派工审核', type: 'warning' })
  if (rolePermissions.has('orders.delivery.review')) summary.push({ label: '可审送货', type: 'danger' })
  if (rolePermissions.has('orders.delivery.sync_price.request')) summary.push({ label: '可申请同步送货单价', type: 'warning' })
  if (rolePermissions.has('orders.delivery.sync_price.review')) summary.push({ label: '可审送货单价同步（含任务中心待办）', type: 'danger' })
  if (rolePermissions.has('orders.delivery.unaudit')) summary.push({ label: '可取消送货审核', type: 'warning' })
  if (rolePermissions.has('orders.delivery.paper_only')) summary.push({ label: '可开空单', type: 'warning' })
  if (rolePermissions.has('orders.sales.delete')) summary.push({ label: '可删除销售订单', type: 'danger' })
  if (rolePermissions.has('orders.sales.unaudit')) summary.push({ label: '可取消审核销售订单', type: 'danger' })
  if (rolePermissions.has('orders.sales.super_correct_quantity')) summary.push({ label: '可超级改数量', type: 'danger' })
  if (rolePermissions.has('orders.sales.super_correct_currency')) summary.push({ label: '可超级改币种', type: 'danger' })
  if (rolePermissions.has('orders.sales.super_correct_dye_fee')) summary.push({ label: '可超级改染费', type: 'danger' })
  if (rolePermissions.has('orders.sales.super_correct_discount')) summary.push({ label: '可超级改折扣', type: 'danger' })
  if (rolePermissions.has('orders.sales.super_correct_unit_price')) summary.push({ label: '可超级改单价', type: 'danger' })
  if (rolePermissions.has('orders.sales.super_correct_order_type')) summary.push({ label: '可超级改订单类型', type: 'danger' })
  if (rolePermissions.has('orders.sales.super_correct_client_po')) summary.push({ label: '可超级改客户采购单号', type: 'danger' })
  if (rolePermissions.has('orders.sales.super_correct_customer')) summary.push({ label: '可超级改客户', type: 'danger' })
  if (rolePermissions.has('orders.sales.super_correct_brand')) summary.push({ label: '可超级改品牌', type: 'danger' })
  if (rolePermissions.has('orders.sales.super_correct_season')) summary.push({ label: '可超级改季节', type: 'danger' })
  if (rolePermissions.has('reports.view')) summary.push({ label: '可看报表中心', type: 'success' })
  if (rolePermissions.has('reports.gain_loss.view')) summary.push({ label: '可看报损报溢核对', type: 'success' })
  if (rolePermissions.has('purchase.orders.review')) summary.push({ label: '可审采购单', type: 'danger' })
  if (rolePermissions.has('purchase.orders.unaudit')) summary.push({ label: '可取消采购审核', type: 'warning' })
  if (rolePermissions.has('billing.statements.unconfirm')) summary.push({ label: '可取消账单确认', type: 'warning' })
  if (rolePermissions.has('inventory.operations.review')) summary.push({ label: '可审库存操作', type: 'danger' })
  if (rolePermissions.has('inventory.initial_stock.review')) summary.push({ label: '可审批期初入库', type: 'danger' })
  return summary
}

/** 仅刷新角色列表（创建新角色后其它页若已挂载，需再拉取才能选到） */
const refreshRoles = async () => {
  try {
    const rRes = await getRoles()
    if (rRes.success) roles.value = rRes.data || []
  } catch (err) {
    console.error(err)
  }
}

const loadAll = async () => {
  loading.value = true
  try {
    const [uRes, rRes, cRes, boardRes] = await Promise.all([
      getUsers({ per_page: 1000, is_active: 'all' }),
      getRoles(),
      getPermissionCatalog(),
      getTaskBoardVisibility()
    ])
    const boardMap = {}
    if (boardRes?.success && Array.isArray(boardRes.data)) {
      for (const row of boardRes.data) {
        boardMap[row.user_id] = Boolean(row.show_on_task_board)
      }
    }
    if (uRes.success) users.value = uRes.data || []
    if (rRes.success) roles.value = rRes.data || []
    if (cRes.success) permissionCatalog.value = cRes.data || {}
    for (const u of users.value) {
      u.show_on_task_board = boardMap[u.user_id] !== false
      try {
        const rr = await getUserRoles(u.user_id)
        u._roles = rr.success ? rr.data : []
      } catch (_) {
        u._roles = []
      }
    }
  } catch (err) {
    ElMessage.error(err.response?.data?.message || '加载权限数据失败')
  } finally {
    loading.value = false
  }
}

async function onBoardVisibilityChange(row, value) {
  if (!row?.user_id) return
  const prev = !value
  boardSavingId.value = row.user_id
  try {
    const resp = await setUserTaskBoardVisibility(row.user_id, value)
    if (!resp?.success) {
      row.show_on_task_board = prev
      ElMessage.error(resp?.message || '保存失败')
    } else {
      ElMessage.success(value ? '已在任务看板显示' : '已从任务看板隐藏')
    }
  } catch (err) {
    row.show_on_task_board = prev
    ElMessage.error(err.response?.data?.message || '保存失败')
  } finally {
    boardSavingId.value = null
  }
}

const openAssign = async (row) => {
  await refreshRoles()
  targetUser.value = row
  selectedRoleIds.value = (row._roles || []).map(r => r.role_id)
  showAssignDialog.value = true
}

const submitAssign = async () => {
  if (!targetUser.value) return
  assigning.value = true
  try {
    const resp = await setUserRoles(targetUser.value.user_id, selectedRoleIds.value)
    if (resp.success) {
      ElMessage.success('角色分配成功')
      showAssignDialog.value = false
      await loadAll()
    } else {
      ElMessage.error(resp.message || '角色分配失败')
    }
  } catch (err) {
    ElMessage.error(err.response?.data?.message || '角色分配失败')
  } finally {
    assigning.value = false
  }
}

const showEffective = async (row) => {
  targetUser.value = row
  try {
    const resp = await getUserEffectivePermissions(row.user_id)
    effectivePermissions.value = resp.success ? (resp.data || []) : []
    activeMatrixModule.value = effectivePermissionMatrix.value[0]?.moduleKey || ''
    showEffectiveDialog.value = true
  } catch (err) {
    ElMessage.error(err.response?.data?.message || '获取生效权限失败')
  }
}

onMounted(loadAll)

defineExpose({ refreshRoles, loadAll })
</script>

<style scoped>
.head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.board-tip {
  margin-bottom: 12px;
}
</style>
